import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { ArrowLeft, Printer } from "lucide-react";

const STATUSES = [
  "pending_payment",
  "pending_transfer",
  "paid",
  "shipped",
  "cancelled",
  "refunded",
];

const STATUS_LABEL = {
  pending_payment: "Pendiente de pago",
  pending_transfer: "Pendiente transferencia",
  paid: "Pagado",
  shipped: "Enviado",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};

function euro(n) {
  return ((n || 0).toFixed(2) + " €").replace(".", ",");
}

export default function AdminOrderDetailPage() {
  const { id } = useParams();
  const { token, checking, authFetch } = useAdminAuth();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await authFetch(`/admin/orders/${id}`);
    setOrder(res.data);
    setTracking(res.data?.tracking || "");
  }, [authFetch, id]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  if (checking)
    return <div className="min-h-screen bg-cream-100 grid place-items-center text-olive-800/60">…</div>;
  if (!token) return <Navigate to="/admin/login" replace />;
  if (!order) return <div className="min-h-screen bg-cream-100 p-10 text-olive-800/60">Cargando pedido…</div>;

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      const body = { status: newStatus };
      if (newStatus === "shipped" && tracking) body.tracking = tracking;
      const res = await authFetch(`/admin/orders/${order.id}/status`, {
        method: "PATCH",
        data: body,
      });
      setOrder(res.data);
    } finally {
      setSaving(false);
    }
  };

  const c = order.customer || {};
  const created = new Date(order.created_at).toLocaleString("es-ES");

  return (
    <main data-testid="admin-order-detail" className="min-h-screen bg-cream-100 text-charcoal">
      {/* Print-only @media — hide everything except .print-area */}
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 16mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .print-area { padding: 0 !important; max-width: 100% !important; }
          .print-area * { color: #1d1d1d !important; }
        }
      `}</style>

      {/* Top bar (hidden on print) */}
      <header className="no-print border-b border-cream-400 bg-cream-100 sticky top-0 z-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
          <Link
            to="/admin"
            data-testid="back-to-admin"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:text-olive-900"
          >
            <ArrowLeft size={14} /> Volver al panel
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="print-albaran"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-olive-700 text-cream-100 text-[11px] uppercase tracking-[0.24em] hover:bg-olive-900"
          >
            <Printer size={14} /> Imprimir albarán
          </button>
        </div>
      </header>

      <section className="print-area max-w-[1100px] mx-auto px-6 lg:px-12 py-10">
        {/* Print header (visible only on print) */}
        <div className="hidden print:block mb-8">
          <p className="text-[10px] uppercase tracking-[0.32em] text-olive-800">Cortijo Santa María</p>
          <p className="text-xs text-olive-800/70">Limones, Moclín · 18247 Granada · info@cortijodesantamaria.com · +34 626 411 029</p>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-olive-700/40 pb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.32em] text-olive-800/60">Albarán de pedido</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-olive-900 mt-2">{order.order_number}</h1>
          </div>
          <div className="text-right text-sm">
            <p className="text-olive-800/60 text-[10px] uppercase tracking-[0.24em]">Fecha</p>
            <p className="text-olive-900">{created}</p>
            <p className="mt-2 text-olive-800/60 text-[10px] uppercase tracking-[0.24em]">Estado</p>
            <p className="text-olive-900 font-medium">{STATUS_LABEL[order.status] || order.status}</p>
          </div>
        </div>

        {/* Customer + shipping */}
        <div className="mt-8 grid sm:grid-cols-2 gap-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">Cliente</p>
            <p className="mt-2 font-serif text-lg text-olive-900">{c.name}</p>
            <p className="text-sm text-olive-800/80">{c.email}</p>
            <p className="text-sm text-olive-800/80">{c.phone}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">Dirección de envío</p>
            <p className="mt-2 text-sm text-olive-900">{c.address1}</p>
            {c.address2 ? <p className="text-sm text-olive-900">{c.address2}</p> : null}
            <p className="text-sm text-olive-900">
              {c.postal_code} {c.city} ({c.province})
            </p>
            <p className="text-sm text-olive-900">{c.country}</p>
          </div>
        </div>

        {/* Items */}
        <table className="w-full mt-10 text-sm">
          <thead>
            <tr className="border-b-2 border-olive-700/40">
              <th className="text-left p-3 text-[10px] uppercase tracking-[0.24em] text-olive-800/60 font-medium">Producto</th>
              <th className="text-center p-3 text-[10px] uppercase tracking-[0.24em] text-olive-800/60 font-medium">Cant.</th>
              <th className="text-right p-3 text-[10px] uppercase tracking-[0.24em] text-olive-800/60 font-medium">Unitario</th>
              <th className="text-right p-3 text-[10px] uppercase tracking-[0.24em] text-olive-800/60 font-medium">Total línea</th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map((it) => (
              <tr key={it.product_id} className="border-b border-cream-400">
                <td className="p-3">
                  <div className="text-olive-900 font-serif">{it.name_es}</div>
                  <div className="text-xs text-olive-800/60">/{it.slug}</div>
                </td>
                <td className="p-3 text-center text-olive-900">{it.qty}</td>
                <td className="p-3 text-right text-olive-900 tabular-nums">{euro(it.unit_price)}</td>
                <td className="p-3 text-right text-olive-900 tabular-nums">{euro(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-6 ml-auto max-w-sm text-sm">
          <div className="flex justify-between py-1.5 text-olive-800/80">
            <span>Subtotal</span>
            <span className="tabular-nums">{euro(order.subtotal)}</span>
          </div>
          <div className="flex justify-between py-1.5 text-olive-800/80">
            <span>Envío</span>
            <span className="tabular-nums">{euro(order.shipping_cost)}</span>
          </div>
          <div className="flex justify-between py-3 border-t-2 border-olive-700/40 mt-2 text-olive-900 font-serif text-lg">
            <span>Total</span>
            <span className="tabular-nums">{euro(order.total)}</span>
          </div>
          <p className="text-xs text-olive-800/60 mt-2">
            Método de pago: {order.payment_method === "stripe" ? "Tarjeta (Stripe)" : "Transferencia bancaria"}{" "}
            · Pago: {order.payment_status || "—"}
          </p>
          {order.tracking ? (
            <p className="text-xs text-olive-800/60 mt-1">Seguimiento: {order.tracking}</p>
          ) : null}
        </div>

        {c.notes ? (
          <div className="mt-10 border-t border-cream-400 pt-5">
            <p className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">Notas del cliente</p>
            <p className="mt-2 text-sm text-olive-800/90 italic">{c.notes}</p>
          </div>
        ) : null}

        {/* Print footer (only visible on print) */}
        <div className="hidden print:block mt-12 pt-4 border-t border-olive-700/30 text-xs text-olive-800/70">
          Gracias por elegir Cortijo Santa María. Este documento es un albarán informativo, no factura fiscal.
        </div>

        {/* Actions (hidden on print) */}
        <div className="no-print mt-10 border border-cream-400 bg-cream-200 p-5 sm:p-6 grid sm:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.24em] text-olive-800/60 mb-1.5">
              Cambiar estado del pedido
            </label>
            <select
              value={order.status}
              onChange={(e) => updateStatus(e.target.value)}
              data-testid="order-status-select"
              disabled={saving}
              className="w-full bg-cream-100 border border-cream-400 px-3 py-2 text-sm text-olive-900"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <p className="text-xs text-olive-800/60 mt-1.5">
              Marcar como <strong>pagado</strong> envía email de confirmación al cliente. Marcar como{" "}
              <strong>enviado</strong> envía email de envío con el seguimiento (si lo introduces).
            </p>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.24em] text-olive-800/60 mb-1.5">
              Número de seguimiento (opcional)
            </label>
            <input
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              data-testid="order-tracking-input"
              placeholder="Ej. SEUR 1234567890"
              className="w-full bg-cream-100 border border-cream-400 px-3 py-2 text-sm text-olive-900"
            />
          </div>
        </div>
      </section>
    </main>
  );
}
